import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Drawer } from "@mui/material";
import BackOfficeLayout from "../../../components/backoffice-layout";
import PaginatorComponent from "../../../components/paginator";
import DateRangePickerComponent from "../../../components/date-range-picker";
import SearchableField from "../../../components/searchable-field";
import LoadingModal from "../../../components/loading-modal";
import CoreAPI from "../../../services/core";
import { useOpenModalTokenExpired } from "../../../contexts";
import { formatDateByString } from "../../../utils/default";

const TYPE_LIST = [
    { id: "Place", label: "Place (Touch Point)" },
    { id: "Event", label: "Event" },
    { id: "Service", label: "Service" },
];

const Container = styled.div`
    width: calc(100% - 32px);
    background-color: #FFFFFF;
    padding: 40px 16px;
    border-radius: 16px;

    .header-box {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 24px;

        .header-label {
            width: fit-content;
            color: #25368D;
            font-size: 20px;
            font-weight: 600;
            line-height: 29.9px;
            text-align: left;
            padding: 2px;
            border-width: 0px 0px 3px 0px;
            border-style: solid;
            border-color: #25368D;
        }

        .export-btn {
            background-color: #25368D;
            color: #FFFFFF;
            font-size: 14px;
            font-weight: 600;
            padding: 12px 24px;
            border-radius: 30px;
            border: none;
            cursor: pointer;
            transition: opacity 0.2s ease;

            &:hover { opacity: 0.85; }
            &:disabled { opacity: 0.5; cursor: not-allowed; }
        }
    }

    .type-tabs {
        display: flex;
        gap: 8px;
        margin-bottom: 24px;
        border-bottom: 1px solid #E5E5E5;
    }

    .type-tab {
        padding: 12px 20px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        color: #505050;
        border-bottom: 3px solid transparent;
        transition: all 0.2s ease;

        &:hover { color: #25368D; }

        &.active {
            color: #25368D;
            font-weight: 600;
            border-bottom-color: #25368D;
        }
    }

    .filter-bar {
        display: flex;
        flex-direction: column;
        gap: 16px;
        margin-bottom: 36px;

        .filters-row {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
            gap: 16px;
            align-items: end;
        }
    }

    .preset-row {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        align-items: center;

        .preset-label {
            color: #505050;
            font-size: 13px;
            font-weight: 500;
            margin-right: 4px;
        }

        .preset-btn {
            padding: 6px 14px;
            border-radius: 20px;
            border: 1px solid #E5E5E5;
            background-color: #FFFFFF;
            color: #505050;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
                border-color: #25368D;
                color: #25368D;
            }

            &.active {
                background-color: #25368D;
                color: #FFFFFF;
                border-color: #25368D;
            }
        }
    }

    .data-box {
        display: flex;
        flex-direction: column;
        gap: 24px;
    }
`;

const Table = styled.div`
    width: 100%;
    border: 1px solid #F2F2F2;
    border-radius: 12px;
    overflow: hidden;

    .row {
        display: grid;
        grid-template-columns: ${(p) => p.$columns || "60px 1.5fr 1.5fr 1.5fr 1.5fr 90px 90px"};
        align-items: center;
        padding: 14px 16px;
        border-bottom: 1px solid #F2F2F2;
        font-size: 14px;
        color: #2A2A2A;
        gap: 8px;

        &.header {
            background-color: #FBE25033;
            color: #25368D;
            font-weight: 600;
        }

        &.data {
            cursor: pointer;
            &:hover { background-color: #FBFBFE; }
        }

        &:last-child { border-bottom: none; }
    }

    .cell-score {
        font-weight: 600;
        color: #25368D;
    }

    .empty-box {
        padding: 48px 16px;
        text-align: center;
        color: #B5B5B5;
        font-size: 14px;
    }
`;

const DrawerContainer = styled.div`
    width: 480px;
    max-width: 100vw;
    padding: 24px;
    color: #2A2A2A;

    .close-btn {
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: #505050;
    }

    .meta-row {
        display: grid;
        grid-template-columns: 140px 1fr;
        gap: 8px;
        padding: 8px 0px;
        border-bottom: 1px dashed #EAEAEA;
        font-size: 13px;

        .meta-key { color: #25368D; font-weight: 600; }
        .meta-val { color: #2A2A2A; }
    }

    .section-title {
        margin-top: 24px;
        margin-bottom: 8px;
        color: #25368D;
        font-size: 14px;
        font-weight: 700;
    }

    .answer-block {
        background-color: #FBFBFE;
        border-radius: 8px;
        padding: 12px 16px;
        margin-bottom: 8px;

        .answer-q {
            font-size: 13px;
            color: #505050;
            margin-bottom: 4px;
        }
        .answer-a {
            font-size: 14px;
            color: #25368D;
            font-weight: 600;
        }
    }

    .empty {
        font-size: 13px;
        color: #B5B5B5;
        padding: 12px 0px;
    }
`;

function ReportPage() {
    const navigate = useNavigate();
    const { setOpenModalTokenExpired } = useOpenModalTokenExpired();

    const [evaluationType, setEvaluationType] = useState("Place");
    const [listForm, setListForm] = useState([]);
    const [listFormSelect, setListFormSelect] = useState([]);
    const [listLocation, setListLocation] = useState([]);
    const [listLocationSelect, setListLocationSelect] = useState([]);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [activePreset, setActivePreset] = useState("this_month");

    const [pageOption, setPageOption] = useState({
        limit: 20, page: 1, total_item: 0, total_page: 1,
    });
    const [listData, setListData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const [drawerRow, setDrawerRow] = useState(null);
    const [drawerDetail, setDrawerDetail] = useState(null);

    useEffect(() => {
        CoreAPI.reportHttpService.setTokenExpiredHandler(setOpenModalTokenExpired);
        CoreAPI.evaluationFormHttpService.setTokenExpiredHandler(setOpenModalTokenExpired);
        CoreAPI.locationHttpService.setTokenExpiredHandler(setOpenModalTokenExpired);
        onInit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function onInit() {
        let _profile = localStorage.getItem("profile");
        try { _profile = JSON.parse(_profile); } catch { }
        if (_profile?.user_username !== "superadmin" && _profile?.user_username !== "admin") {
            navigate(`/account`);
            return;
        }
        setIsLoading(true);
        await Promise.all([onLoadForms(), onLoadLocations()]);
        await onFetch({
            evaluation_type: evaluationType,
            limit: pageOption.limit, page: 1,
            formId: "", locationId: "", start: "", end: "",
        });
        setIsLoading(false);
    }

    async function onLoadForms() {
        let response = null;
        try {
            response = await CoreAPI.evaluationFormHttpService.evaluationFormGetListByParam({
                search: "",
                search_by: ["evaluation_name"],
                order: "ASC",
                order_by: "evaluation_name",
                limit: 1000, page: 1,
                type: [], department: [], division: [], province: [], location: [],
            });
        } catch { }
        const rows = Array.isArray(response?.data) ? response.data : [];
        setListForm(rows.map(r => ({
            id: r.evaluation_form_evaluation_form_id,
            label: r.evaluation_form_evaluation_name || "-",
            type: r.evaluation_form_evaluation_type,
        })));
    }

    async function onLoadLocations() {
        // Use the lightweight GET /location/list endpoint — list-by-param has
        // an N+1 that fetches every form per location and is unusable for a dropdown.
        let response = null;
        try {
            response = await CoreAPI.locationHttpService.locationList();
        } catch { }
        const rows = Array.isArray(response) ? response : [];
        const sorted = [...rows].sort((a, b) =>
            (a.location_name || "").localeCompare(b.location_name || "", "th"));
        setListLocation(sorted.map(r => ({
            id: r.location_id,
            label: r.location_name || "-",
        })));
    }

    function toStartIso(d) {
        if (!d) return null;
        const x = new Date(d); x.setHours(0, 0, 0, 0); return x.toISOString();
    }
    function toEndIso(d) {
        if (!d) return null;
        const x = new Date(d); x.setHours(23, 59, 59, 999); return x.toISOString();
    }

    async function onFetch({ evaluation_type, limit, page, formId, locationId, start, end }) {
        const startIso = start ? toStartIso(start) : toStartIso(defaultStartDate());
        const endIso = end ? toEndIso(end) : toEndIso(defaultEndDate());

        let response = null;
        try {
            response = await CoreAPI.reportHttpService.listEvaluationReport({
                evaluation_type,
                evaluation_form_id: formId || undefined,
                location_id: locationId || undefined,
                start_date: startIso,
                end_date: endIso,
                limit, page,
            });
        } catch { }
        if (response) {
            setPageOption({
                limit: response.limit,
                page: response.page,
                total_item: response.total_item,
                total_page: response.total_page,
            });
            setListData(Array.isArray(response.data) ? response.data : []);
        } else {
            setListData([]);
            setPageOption(prev => ({ ...prev, total_item: 0, total_page: 1 }));
        }
    }

    function defaultStartDate() {
        const d = new Date(); d.setDate(1); return d;
    }
    function defaultEndDate() {
        const d = new Date(); d.setMonth(d.getMonth() + 1, 0); return d;
    }

    function rangeForPreset(preset) {
        const today = new Date();
        const y = today.getFullYear();
        const m = today.getMonth();
        switch (preset) {
            case "this_month":
                return { start: new Date(y, m, 1), end: new Date(y, m + 1, 0) };
            case "last_month":
                return { start: new Date(y, m - 1, 1), end: new Date(y, m, 0) };
            case "this_year":
                return { start: new Date(y, 0, 1), end: new Date(y, 11, 31) };
            case "last_year":
                return { start: new Date(y - 1, 0, 1), end: new Date(y - 1, 11, 31) };
            default:
                return { start: null, end: null };
        }
    }

    function buildFilterParams(overrides = {}) {
        return {
            evaluation_type: evaluationType,
            limit: pageOption.limit,
            page: pageOption.page,
            formId: (listFormSelect[0]?.id) || "",
            locationId: (listLocationSelect[0]?.id) || "",
            start: dateRange.start,
            end: dateRange.end,
            ...overrides,
        };
    }

    async function handleChangeType(typeId) {
        setEvaluationType(typeId);
        setListFormSelect([]);
        setListLocationSelect([]);
        setIsLoading(true);
        await onFetch(buildFilterParams({ evaluation_type: typeId, page: 1, formId: "", locationId: "" }));
        setIsLoading(false);
    }

    async function handleChangeForm(picked) {
        setListFormSelect(picked);
        setIsLoading(true);
        await onFetch(buildFilterParams({ page: 1, formId: (picked[0]?.id) || "" }));
        setIsLoading(false);
    }

    async function handleChangeLocation(picked) {
        setListLocationSelect(picked);
        setIsLoading(true);
        await onFetch(buildFilterParams({ page: 1, locationId: (picked[0]?.id) || "" }));
        setIsLoading(false);
    }

    async function handleChangeDateRange(v) {
        setDateRange(v);
        setActivePreset(null);
        if ((v.start && v.end) || (!v.start && !v.end)) {
            setIsLoading(true);
            await onFetch(buildFilterParams({ page: 1, start: v.start, end: v.end }));
            setIsLoading(false);
        }
    }

    async function handlePreset(preset) {
        const range = rangeForPreset(preset);
        setDateRange(range);
        setActivePreset(preset);
        setIsLoading(true);
        await onFetch(buildFilterParams({ page: 1, start: range.start, end: range.end }));
        setIsLoading(false);
    }

    async function handleChangePageOption(param) {
        setIsLoading(true);
        await onFetch(buildFilterParams({ limit: param.limit, page: param.page }));
        setIsLoading(false);
    }

    async function handleRowClick(row) {
        setDrawerRow(row);
        setDrawerDetail(null);
        let response = null;
        try {
            response = await CoreAPI.reportHttpService.detailEvaluationReport({
                evaluator_history_id: row.evaluator_history_id,
            });
        } catch { }
        setDrawerDetail(response?.data || null);
    }

    async function handleExport() {
        setIsExporting(true);
        try {
            const startIso = dateRange.start ? toStartIso(dateRange.start) : toStartIso(defaultStartDate());
            const endIso = dateRange.end ? toEndIso(dateRange.end) : toEndIso(defaultEndDate());
            const formId = (listFormSelect[0]?.id) || "";
            const locationId = (listLocationSelect[0]?.id) || "";
            const res = await CoreAPI.reportHttpService.exportEvaluationReport({
                evaluation_type: evaluationType,
                evaluation_form_id: formId || undefined,
                location_id: locationId || undefined,
                start_date: startIso,
                end_date: endIso,
            });
            const blob = res?.data;
            const cd = res?.headers?.["content-disposition"] || "";
            const match = cd.match(/filename="?([^"]+)"?/i);
            const filename = (match && match[1]) || `sat-eval-${evaluationType}.xlsx`;
            triggerBlobDownload(blob, filename);
        } catch (err) {
            console.error("export failed", err);
            alert("ดาวน์โหลดล้มเหลว กรุณาลองใหม่อีกครั้ง");
        }
        setIsExporting(false);
    }

    function triggerBlobDownload(blob, filename) {
        if (!(blob instanceof Blob)) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    // ============ render ============

    const COLUMN_DEFS = {
        Place:   { columns: "60px 1.4fr 1.6fr 1.6fr 100px 100px", labels: ["#", "Submitted", "Evaluator", "Form / Location", "Sat.", "Exp."] },
        Event:   { columns: "60px 1.4fr 1.6fr 1.6fr 100px 100px", labels: ["#", "Submitted", "Evaluator", "Event (Form / Window)", "Sat.", "Exp."] },
        Service: { columns: "60px 1.4fr 1.6fr 1.6fr 100px 100px", labels: ["#", "Submitted", "Evaluator", "Form / Submitted From", "Sat.", "Exp."] },
    };

    function renderRow(row, index) {
        const displayIndex = (pageOption.page - 1) * pageOption.limit + index + 1;
        const submitted = row.created_at ? formatDateByString(row.created_at, "DD/MM/YYYY HH:mm") : "-";

        let formCol = row.evaluation_name || "-";
        if (evaluationType === "Place") {
            formCol = `${row.evaluation_name || "-"} · ${row.location_name || "-"}`;
        } else if (evaluationType === "Event") {
            const win = row.datetime_start && row.datetime_end
                ? `${formatDateByString(row.datetime_start, "DD/MM HH:mm")} → ${formatDateByString(row.datetime_end, "DD/MM HH:mm")}`
                : "";
            formCol = `${row.evaluation_name || "-"}${win ? " · " + win : ""}`;
        } else {
            const from = formatSubmissionLocation(row.submission_location, row.evaluator_province);
            formCol = `${row.evaluation_name || "-"}${from ? " · " + from : ""}`;
        }

        return (
            <div key={row.evaluator_history_id} className="row data" onClick={() => handleRowClick(row)}>
                <div>{displayIndex}</div>
                <div>{submitted}</div>
                <div>{row.evaluator_name || "-"}</div>
                <div>{formCol}</div>
                <div className="cell-score">{row.satisfaction_avg != null ? Number(row.satisfaction_avg).toFixed(2) : "-"}</div>
                <div className="cell-score">{row.expectation_avg != null ? Number(row.expectation_avg).toFixed(2) : "-"}</div>
            </div>
        );
    }

    return (
        <BackOfficeLayout>
            <LoadingModal open={isLoading} />
            <Container>
                <div className="header-box">
                    <div className="header-label">
                        Evaluation Report {pageOption.total_item !== 0 ? `(${pageOption.total_item})` : ""}
                    </div>
                    <button
                        className="export-btn"
                        onClick={handleExport}
                        disabled={isExporting || listData.length === 0}
                    >
                        {isExporting ? "กำลังดาวน์โหลด..." : "Export Excel"}
                    </button>
                </div>

                <div className="type-tabs">
                    {TYPE_LIST.map(t => (
                        <div
                            key={t.id}
                            className={`type-tab ${evaluationType === t.id ? "active" : ""}`}
                            onClick={() => handleChangeType(t.id)}
                        >
                            {t.label}
                        </div>
                    ))}
                </div>

                <div className="filter-bar">
                    <div className="filters-row">
                        <SearchableField
                            label={"แบบสอบถาม"}
                            placeholder={"ทั้งหมด — พิมพ์เพื่อค้นหา"}
                            options={listForm.filter(f => f.type === evaluationType)}
                            value={listFormSelect[0] || null}
                            onChange={(picked) => handleChangeForm(picked ? [picked] : [])}
                        />
                        <SearchableField
                            label={"สถานที่"}
                            placeholder={"ทั้งหมด — พิมพ์เพื่อค้นหา"}
                            options={listLocation}
                            value={listLocationSelect[0] || null}
                            onChange={(picked) => handleChangeLocation(picked ? [picked] : [])}
                        />
                        <DateRangePickerComponent
                            label={"ช่วงวันที่"}
                            placeholder={"เลือกช่วงวันที่"}
                            value={dateRange}
                            onChange={handleChangeDateRange}
                        />
                    </div>
                    <div className="preset-row">
                        <span className="preset-label">เลือกช่วงด่วน:</span>
                        {[
                            { id: "this_month", label: "เดือนนี้" },
                            { id: "last_month", label: "เดือนที่แล้ว" },
                            { id: "this_year", label: "ปีนี้" },
                            { id: "last_year", label: "ปีที่แล้ว" },
                        ].map(p => (
                            <button
                                key={p.id}
                                className={`preset-btn ${activePreset === p.id ? "active" : ""}`}
                                onClick={() => handlePreset(p.id)}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="data-box">
                    <Table $columns={COLUMN_DEFS[evaluationType].columns}>
                        <div className="row header">
                            {COLUMN_DEFS[evaluationType].labels.map((l, i) => (
                                <div key={i}>{l}</div>
                            ))}
                        </div>
                        {listData.length === 0 && !isLoading && (
                            <div className="empty-box">ไม่พบข้อมูล</div>
                        )}
                        {listData.map(renderRow)}
                    </Table>
                    <PaginatorComponent
                        limit={pageOption.limit}
                        page={pageOption.page}
                        total_item={pageOption.total_item}
                        total_page={pageOption.total_page}
                        onChangePageOption={handleChangePageOption}
                    />
                </div>
            </Container>

            <Drawer
                anchor="right"
                open={Boolean(drawerRow)}
                onClose={() => { setDrawerRow(null); setDrawerDetail(null); }}
            >
                <DrawerDetail row={drawerRow} detail={drawerDetail} onClose={() => { setDrawerRow(null); setDrawerDetail(null); }} />
            </Drawer>
        </BackOfficeLayout>
    );
}

function DrawerDetail({ row, detail, onClose }) {
    if (!row) return null;
    const submitted = row.created_at ? formatDateByString(row.created_at, "DD/MM/YYYY HH:mm") : "-";

    const answers = collectAnswers(detail?.evaluation_data);

    return (
        <DrawerContainer>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div style={{ color: "#25368D", fontSize: 18, fontWeight: 700 }}>รายละเอียดคำตอบ</div>
                <button className="close-btn" onClick={onClose}>✕</button>
            </div>

            <div className="meta-row"><div className="meta-key">Submitted</div><div className="meta-val">{submitted}</div></div>
            <div className="meta-row"><div className="meta-key">Form</div><div className="meta-val">{row.evaluation_name || "-"}</div></div>
            <div className="meta-row"><div className="meta-key">Type</div><div className="meta-val">{row.evaluation_type || "-"}</div></div>
            <div className="meta-row"><div className="meta-key">Evaluator</div><div className="meta-val">{row.evaluator_name || "-"}</div></div>
            <div className="meta-row"><div className="meta-key">LINE User ID</div><div className="meta-val">{row.evaluator_line_id || "-"}</div></div>
            <div className="meta-row"><div className="meta-key">Province</div><div className="meta-val">{row.evaluator_province || "-"}</div></div>
            {row.location_name && (
                <div className="meta-row"><div className="meta-key">Location</div><div className="meta-val">{row.location_name}</div></div>
            )}
            <div className="meta-row"><div className="meta-key">Satisfaction Avg</div><div className="meta-val">{row.satisfaction_avg != null ? Number(row.satisfaction_avg).toFixed(2) : "-"}</div></div>
            <div className="meta-row"><div className="meta-key">Expectation Avg</div><div className="meta-val">{row.expectation_avg != null ? Number(row.expectation_avg).toFixed(2) : "-"}</div></div>

            <div className="section-title">Answers</div>
            {!detail && <div className="empty">กำลังโหลด...</div>}
            {detail && answers.length === 0 && <div className="empty">ไม่มีคำตอบ</div>}
            {detail && answers.map((a, i) => (
                <div key={i} className="answer-block">
                    <div className="answer-q">{a.question || "-"}</div>
                    <div className="answer-a">{formatAnswer(a.answer)}</div>
                </div>
            ))}
        </DrawerContainer>
    );
}

function collectAnswers(evaluation_data) {
    const out = [];
    if (!evaluation_data || typeof evaluation_data !== "object") return out;
    const form = Array.isArray(evaluation_data.evaluation_form) ? evaluation_data.evaluation_form : [];
    for (const group of form) {
        const questions = Array.isArray(group?.question) ? group.question
                        : Array.isArray(group?.questions) ? group.questions
                        : [];
        for (const q of questions) {
            out.push({
                question: q?.question_title || q?.title || q?.tag || "-",
                answer: q?.answer,
            });
        }
        if (Array.isArray(group?.sub_group)) {
            for (const sg of group.sub_group) {
                const sgQs = Array.isArray(sg?.question) ? sg.question
                           : Array.isArray(sg?.questions) ? sg.questions
                           : [];
                for (const q of sgQs) {
                    out.push({
                        question: q?.question_title || q?.title || q?.tag || "-",
                        answer: q?.answer,
                    });
                }
            }
        }
    }
    return out;
}

function formatAnswer(a) {
    if (a === null || a === undefined || a === "") return "-";
    if (typeof a === "object") {
        if (Array.isArray(a)) return a.map(formatAnswer).join(", ");
        const v = a.value ?? a.score ?? a.label ?? a.text;
        return v !== undefined ? formatAnswer(v) : JSON.stringify(a);
    }
    return String(a);
}

function formatSubmissionLocation(loc, fallback) {
    if (!loc) return fallback || "";
    if (typeof loc === "string") return loc;
    if (typeof loc === "object") {
        const province = loc.province || fallback || "";
        const district = loc.district || loc.amphoe || "";
        return [province, district].filter(Boolean).join(" / ");
    }
    return fallback || "";
}

export default ReportPage;
